import React, { useEffect } from "react";
import lockIcon from "assets/icons/unlock-fill.svg";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import { getOperationDetails } from "store/payments/operations/slice";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import { formatDateTime, useTimeFormat } from "helpers/timeFormat";
import styles from "./OperationsDetailsTab.module.scss";
import { buildProviderExplanation, hasValue } from "./providerOutcome";

type Props = {
  operationId: number;
};

const OperationsDetailsTab: React.FC<Props> = ({ operationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const timeFormat = useTimeFormat();

  const handleData = (..._args: unknown[]) => {};

  const { operationDetails: operation, loadingDetails } = useAppSelector(
    (store) => store.paymentsOperations,
  );

  useEffect(() => {
    if (operationId) {
      dispatch(getOperationDetails(operationId));
    }
  }, [operationId, dispatch]);

  const itemPayload = {
    id: operation?.id ?? operationId,
    publicId: operation?.publicId ?? "",
    user: operation?.user
      ? operation.user.email || operation.user.firstName
      : `User ${operation?.userId ?? ""}`,
    type: `${operation?.type ?? ""}${operation?.origin === "backoffice_manual" ? ` · ${t("manual")}` : ""}`,
    reasonCode: operation?.reasonCode ?? "",
    statusCode: operation?.statusCode ?? "",
    amount: operation?.amount ?? "",
    currency: operation?.currency ?? "",
    externalRef: operation?.externalRef ?? "",
    referenceType: operation?.referenceType ?? "",
    referenceId: operation?.referenceId ?? "",
    methodCode: operation?.methodCode ?? "",
    processingType: operation?.processingType ?? "",
    completionMode: operation?.completionMode ?? "",
    created: operation?.created ?? "",
    completed: operation?.completed ?? "",
  };

  const providerOutcome = operation?.providerOutcome;
  const providerExplanation = providerOutcome
    ? buildProviderExplanation(
        providerOutcome,
        operation?.processingType === "internal" ||
          operation?.completionMode === "manual" ||
          operation?.methodCode === "internal",
      )
    : "";
  const showProviderDetails =
    Boolean(providerOutcome) && hasValue(providerExplanation);
  const providerFinalStatus = (
    providerOutcome?.terminalStatusCode ||
    providerOutcome?.statusCode ||
    ""
  ).toLowerCase();

  if (loadingDetails) {
    return <ModalTabSkeleton />;
  }

  return (
    <div className="operation-content-wrapper">
      <div className="operations-main-form-wrapper">
        <div className="operations-form-box">
          <div className="operations-form-box-item row">
            <InnerInput
              value={itemPayload?.id?.toString() ?? ""}
              placeholder={itemPayload?.id ? t("id") : ""}
              label={t("id")}
              type="text"
              id="operation-id"
              onChange={(e) => handleData("id", e)}
              disabled
              icon={lockIcon}
            />
            <InnerInput
              value={itemPayload?.user ?? ""}
              placeholder={itemPayload?.user ? t("user") : ""}
              label={t("user")}
              type="text"
              id="operation-user"
              onChange={(e) => handleData("user", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item">
            <InnerInput
              value={itemPayload?.reasonCode ?? ""}
              placeholder={itemPayload?.reasonCode ? t("reasonCode") : ""}
              label={t("reasonCode")}
              type="text"
              id="operation-reason-code"
              onChange={(e) => handleData("reasonCode", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item row">
            <InnerInput
              value={String(itemPayload?.amount ?? "")}
              placeholder={itemPayload?.amount ? t("amount") : ""}
              label={t("amount")}
              type="text"
              id="operation-amount"
              onChange={(e) => handleData("amount", e)}
              disabled
              icon={lockIcon}
            />
            <InnerInput
              value={itemPayload?.currency ?? ""}
              placeholder={itemPayload?.currency ? t("currency") : ""}
              label={t("currency")}
              type="text"
              id="operation-currency"
              onChange={(e) => handleData("currency", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item row">
            <InnerInput
              value={itemPayload?.referenceType ?? ""}
              placeholder={itemPayload?.referenceType ? t("referenceType") : ""}
              label={t("referenceType")}
              type="text"
              id="operation-reference-type"
              onChange={(e) => handleData("referenceType", e)}
              disabled
              icon={lockIcon}
            />
            <InnerInput
              value={String(itemPayload?.referenceId ?? "")}
              placeholder={itemPayload?.referenceId ? t("referenceId") : ""}
              label={t("referenceId")}
              type="text"
              id="operation-reference-id"
              onChange={(e) => handleData("referenceId", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item row">
            <InnerInput
              value={itemPayload?.methodCode ?? ""}
              placeholder={itemPayload?.methodCode ? t("methodCode") : ""}
              label={t("methodCode")}
              type="text"
              id="operation-method-code"
              onChange={(e) => handleData("methodCode", e)}
              disabled
              icon={lockIcon}
            />

            <InnerInput
              value={itemPayload?.processingType ?? ""}
              placeholder={
                itemPayload?.processingType ? t("processingType") : ""
              }
              label={t("processingType")}
              type="text"
              id="operation-processing-type"
              onChange={(e) => handleData("processingType", e)}
              disabled
              icon={lockIcon}
            />
          </div>
        </div>

        <div className="operations-form-box">
          <div className="operations-form-box-item">
            <InnerInput
              value={itemPayload?.publicId ?? ""}
              placeholder={itemPayload?.publicId ? t("publicId") : ""}
              label={t("publicId")}
              type="text"
              id="operation-public-id"
              onChange={(e) => handleData("publicId", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item row">
            <InnerInput
              value={itemPayload?.type ?? ""}
              placeholder={itemPayload?.type ? t("type") : ""}
              label={t("type")}
              type="text"
              id="operation-type"
              onChange={(e) => handleData("type", e)}
              disabled
              icon={lockIcon}
            />
            <InnerInput
              value={itemPayload?.statusCode ?? ""}
              placeholder={itemPayload?.statusCode ? t("statusCode") : ""}
              label={t("statusCode")}
              type="text"
              id="operation-status-code"
              onChange={(e) => handleData("statusCode", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item">
            <InnerInput
              value={itemPayload?.externalRef ?? ""}
              placeholder={itemPayload?.externalRef ? t("externalRef") : ""}
              label={t("externalRef")}
              type="text"
              id="operation-external-ref"
              onChange={(e) => handleData("externalRef", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item">
            <InnerInput
              value={
                itemPayload?.created
                  ? formatDateTime(itemPayload.created, timeFormat)
                  : ""
              }
              placeholder={itemPayload?.created ? t("created") : ""}
              label={t("created")}
              type="text"
              id="operation-created"
              onChange={(e) => handleData("created", e)}
              disabled
              icon={lockIcon}
            />
          </div>

          <div className="operations-form-box-item row">
            <InnerInput
              value={itemPayload?.completionMode ?? ""}
              placeholder={
                itemPayload?.completionMode ? t("completionMode") : ""
              }
              label={t("completionMode")}
              type="text"
              id="operation-completion-mode"
              onChange={(e) => handleData("completionMode", e)}
              disabled
              icon={lockIcon}
            />
            <InnerInput
              value={
                itemPayload?.completed
                  ? formatDateTime(itemPayload.completed, timeFormat)
                  : ""
              }
              placeholder={itemPayload?.completed ? t("completed") : ""}
              label={t("completed")}
              type="text"
              id="operation-completed"
              onChange={(e) => handleData("completed", e)}
              disabled
              icon={lockIcon}
            />
          </div>
        </div>
      </div>

      {showProviderDetails && (
        <section className={styles.providerDetails}>
          <h3 className={styles.providerDetailsTitle}>{t("providerDetails")}</h3>
          <div className={styles.providerDetailsGrid}>
            {providerExplanation && (
              <div
                className={`${styles.providerDetailsExplanation} ${
                  providerFinalStatus === "completed"
                    ? styles.advancedInfoCompleted
                    : providerFinalStatus === "declined"
                      ? styles.advancedInfoDeclined
                      : ""
                }`}
              >
                <InnerInput
                  value={providerExplanation}
                  label={t("advancedInfo")}
                  type="textarea"
                  id="provider-advanced-info"
                  onChange={handleData}
                  disabled
                  icon={lockIcon}
                  rows={4}
                />
                <CopyToClipboard
                  text={providerExplanation}
                  notificationMessage=""
                  className={styles.advancedInfoCopyButton}
                />
              </div>
            )}

          </div>
        </section>
      )}
    </div>
  );
};

export default OperationsDetailsTab;
